import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  TrendingUp, TrendingDown, DollarSign, ShoppingCart,
  Calendar, Users, BarChart3, Package, RefreshCw, ArrowUpRight
} from 'lucide-react';
import { adminFetch } from '../../utils/adminApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PERIODS = [
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
  { value: '90d', label: '90 Days' },
];

function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/analytics?period=${period}`);
      if (res.ok) setData(await res.json());
      else toast.error('Failed to load analytics');
    } catch { toast.error('Error loading analytics'); }
    finally { setLoading(false); }
  }, [period]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="analytics-loading">
        <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  const s = data?.summary || {};

  return (
    <div data-testid="admin-analytics-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="analytics-title">Analytics Dashboard</h1>
          <p className="text-gray-500 text-sm">Business performance overview</p>
        </div>
        <div className="flex items-center gap-2">
          {PERIODS.map(p => (
            <button key={p.value} onClick={() => setPeriod(p.value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                period === p.value ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`} data-testid={`period-${p.value}`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <MetricCard title="Revenue" value={`\u20B9${(s.total_revenue || 0).toLocaleString()}`}
          change={s.revenue_change} icon={DollarSign} color="blue" testId="metric-revenue" />
        <MetricCard title="Orders" value={s.total_orders || 0}
          change={s.orders_change} icon={ShoppingCart} color="green" testId="metric-orders" />
        <MetricCard title="Avg Order Value" value={`\u20B9${(s.avg_order_value || 0).toLocaleString()}`}
          icon={TrendingUp} color="purple" testId="metric-aov" />
        <MetricCard title="Bookings" value={s.total_bookings || 0}
          icon={Calendar} color="orange" testId="metric-bookings" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        {/* Revenue Trend */}
        <div className="bg-white rounded-xl shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <BarChart3 size={16} className="text-blue-600" /> Revenue Trend
          </h3>
          {(data?.revenue_trend || []).length === 0 ? (
            <p className="text-gray-400 text-sm py-8 text-center">No data for this period</p>
          ) : (
            <div className="space-y-2" data-testid="revenue-trend">
              {(data?.revenue_trend || []).slice(-10).map((d, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-xs text-gray-500 w-20 flex-shrink-0">{d.date}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                    <div className="bg-blue-500 h-full rounded-full transition-all"
                      style={{ width: `${Math.min(100, (d.revenue / (Math.max(...(data?.revenue_trend || []).map(x => x.revenue)) || 1)) * 100)}%` }} />
                  </div>
                  <span className="text-xs font-medium text-gray-700 w-24 text-right">{'\u20B9'}{d.revenue.toLocaleString()}</span>
                  <span className="text-xs text-gray-400 w-12 text-right">{d.orders} ord</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Order Status */}
        <div className="bg-white rounded-xl shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Package size={16} className="text-green-600" /> Order Status Breakdown
          </h3>
          <div className="space-y-3" data-testid="order-statuses">
            {(data?.order_statuses || []).map((d, i) => {
              const total = (data?.order_statuses || []).reduce((a, b) => a + b.count, 0);
              const pct = total > 0 ? ((d.count / total) * 100).toFixed(1) : 0;
              const colors = {
                pending: 'bg-yellow-500', confirmed: 'bg-blue-500', processing: 'bg-indigo-500',
                shipped: 'bg-purple-500', delivered: 'bg-green-500', cancelled: 'bg-red-500',
                payment_failed: 'bg-red-400', refund_initiated: 'bg-orange-500'
              };
              return (
                <div key={i} className="flex items-center gap-3">
                  <span className={`w-3 h-3 rounded-full ${colors[d.status] || 'bg-gray-400'} flex-shrink-0`} />
                  <span className="text-sm text-gray-700 capitalize flex-1">{d.status?.replace(/_/g, ' ')}</span>
                  <span className="text-sm font-semibold text-gray-900">{d.count}</span>
                  <span className="text-xs text-gray-400 w-12 text-right">{pct}%</span>
                </div>
              );
            })}
            {(data?.order_statuses || []).length === 0 && (
              <p className="text-gray-400 text-sm py-4 text-center">No orders in this period</p>
            )}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        {/* Top Products */}
        <div className="bg-white rounded-xl shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <ArrowUpRight size={16} className="text-purple-600" /> Top Products
          </h3>
          <div className="space-y-2.5" data-testid="top-products">
            {(data?.top_products || []).map((p, i) => (
              <div key={i} className="flex items-center gap-3 py-1.5">
                <span className="w-6 h-6 bg-gray-100 text-gray-600 text-xs font-bold rounded-full flex items-center justify-center flex-shrink-0">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{p.name}</div>
                  <div className="text-xs text-gray-500">{p.quantity} units sold</div>
                </div>
                <span className="text-sm font-semibold text-gray-900">{'\u20B9'}{p.revenue.toLocaleString()}</span>
              </div>
            ))}
            {(data?.top_products || []).length === 0 && (
              <p className="text-gray-400 text-sm py-4 text-center">No product data</p>
            )}
          </div>
        </div>

        {/* Payment Methods */}
        <div className="bg-white rounded-xl shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <DollarSign size={16} className="text-green-600" /> Payment Methods
          </h3>
          <div className="space-y-3" data-testid="payment-methods">
            {(data?.payment_methods || []).map((p, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                <div>
                  <div className="text-sm font-medium text-gray-900 capitalize">{p.method?.replace(/_/g, ' ')}</div>
                  <div className="text-xs text-gray-500">{p.count} orders</div>
                </div>
                <span className="text-sm font-semibold text-gray-900">{'\u20B9'}{p.total.toLocaleString()}</span>
              </div>
            ))}
            {(data?.payment_methods || []).length === 0 && (
              <p className="text-gray-400 text-sm py-4 text-center">No payment data</p>
            )}
          </div>

          <div className="mt-4 pt-4 border-t">
            <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Calendar size={14} className="text-orange-600" /> Booking Status
            </h4>
            <div className="space-y-2" data-testid="booking-statuses">
              {(data?.booking_statuses || []).map((b, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 capitalize">{b.status?.replace(/_/g, ' ')}</span>
                  <span className="text-sm font-semibold text-gray-900">{b.count}</span>
                </div>
              ))}
              {(data?.booking_statuses || []).length === 0 && (
                <p className="text-gray-400 text-xs text-center">No bookings</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ title, value, change, icon: Icon, color, testId }) {
  const colorMap = {
    blue: 'bg-blue-50 text-blue-600', green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600', orange: 'bg-orange-50 text-orange-600'
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-4" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-gray-500 uppercase">{title}</span>
        <div className={`p-2 rounded-lg ${colorMap[color]}`}>
          <Icon size={16} />
        </div>
      </div>
      <div className="text-xl sm:text-2xl font-bold text-gray-900">{value}</div>
      {change !== undefined && change !== null && (
        <div className={`flex items-center gap-1 mt-1 text-xs font-medium ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {change >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
          {Math.abs(change).toFixed(1)}% vs prev period
        </div>
      )}
    </div>
  );
}

export default AdminAnalytics;
